import {Component, EventEmitter, OnInit, Output, Input} from '@angular/core';
import {FmcElementsComponent} from '../fmc-elements.component';
import {FormsModule, ReactiveFormsModule, UntypedFormGroup, Validators} from '@angular/forms';
import {CrumbsSearchModel} from './crumbs-search.model';
import {debounceTime} from 'rxjs/operators';
import {NgFor, NgIf} from '@angular/common';
import {MatFormFieldModule} from '@angular/material/form-field';
import {MatInputModule} from '@angular/material/input';
import {MatButtonModule} from '@angular/material/button';
import {MatProgressSpinnerModule} from '@angular/material/progress-spinner';
import {MatAutocompleteModule} from '@angular/material/autocomplete';
import {MatSelectModule} from '@angular/material/select';
import {MatIconModule} from '@angular/material/icon';
import {SensisMobileComponent} from '../sensis-mobile/sensis-mobile.component';
import {SensisEmailComponent} from '../sensis-email/sensis-email.component';
import {fmcValidator} from '../common';

const PARTY_ROLE_SUITES_TYPES = 'party-role-suites';
const PARTY_ROLE_TYPES = 'party-role-types';
const PARTY_ROLE_CATEGORIES = 'party-role-categories';
const OWNER_RELATIONSHIP_TYPE = 13;
const PRIMARY_CONTACT_RELATIONSHIP_TYPE = 18;

const getIdFromTypes = (type, slug) => {
  const match = type.find(t => t.ext && t.ext.Slug === slug);
  return (match) ? match.id : null;
};

@Component({
  selector: 'fmc-crumb-search',
  templateUrl: './crumb-search.component.html',
  styleUrls : ['./crumb-search.component.css'],
  standalone: true,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    NgIf,
    NgFor,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatProgressSpinnerModule,
    MatAutocompleteModule,
    MatSelectModule,
    MatIconModule,
    SensisMobileComponent,
    SensisEmailComponent
  ]
})
export class CrumbSearchComponent extends FmcElementsComponent implements OnInit {
  public defaultControl = new CrumbsSearchModel().crumbsAccountForm;
  public defaultControlType = 'FormGroup';
  public formGroup: UntypedFormGroup;
  public loadingResults = false;
  public selectedCrumbRecord: any;
  public partyRolesSuites: Array<any> = [];
  public displayAccountForm = false;
  public displayPrimaryContacts = false;
  public displayPrimaryForm = false;
  public contacts: any;
  public selectedCompany: any;
  public selectedContact: any;
  public submitted = true;

  @Output() public crumbResults = new EventEmitter<any>();
  @Input() public partyRoleCategory: string;
  @Input() public partyRoleType: string;
  @Input() public partyRoleSuite: string;
  @Input() public allowContactSelection = true;
  @Input() public companyPartyRoleId = null;
  @Input() public contactPartyRoleId = null;

  public contactDetails = {
    phone: null,
    email: null,
    address: null
  };
  public loading = false;

  public resultsPacket: any = {
    companyPartyRoleId: null,
    contactPartyRoleId: null
  };

  private partyRoleSuitesTypes: Array<any> = [];
  private partyRoleTypes: Array<any> = [];
  private partyRoleCategories: Array<any> = [];
  private currentSearch = '';

  ngOnInit() {
    super.ngOnInit();
    this.populateTypes();

    if (!this.partyRoleType && !this.partyRoleSuite && !this.partyRoleCategory) {
      console.error('Crumbs Search', '[missing party role type] OR [party role suite]');
    }

    this.formGroup.get('company')
      .valueChanges
      .pipe(debounceTime(500))
      .subscribe((result) => {
        if (result && result !== this.currentSearch) {
          this.searchPartyRoleSuite(result);
        }
        this.currentSearch = result;
      });

    // initialize the results to the inputs or FG inputs
    this.resultsPacket.companyPartyRoleId = this.companyPartyRoleId || this.formGroup.get('companyPartyRoleId').value;
    this.resultsPacket.contactPartyRoleId = this.contactPartyRoleId || this.formGroup.get('contactPartyRoleId').value;


    const onCompanyChange = (val) => {
      if (val && !this.formGroup.value.company) {
        // if there is no selected company -- query from DB and select that
        this.clientService.getPartyId(val).subscribe((partyRole: any) => {
          // console.log('party selected');
          // do search
          // this.formGroup.get('company').patchValue({}) // do the search
          this.clientService.getPartyMember(partyRole.PartyID).subscribe((party: any) => {
            this.selectedCompany = {
              PartyName: party.party.name,
              PartyRoleID: val
            };
            this.formGroup.patchValue({company: this.selectedCompany});
            this.partyRolesSuites = [this.selectedCompany];
            this.selectCompany(this.selectedCompany);
          });
        });
      }
    }

    onCompanyChange(this.formGroup.get('companyPartyRoleId').value);
    this.formGroup.get('companyPartyRoleId').valueChanges.subscribe((val) => {
      // console.log('Company has changed', val);
      onCompanyChange(val);
    });


    const onContactChange = (val) => {
      // console.log('Contact has changed', val);
      if (val && !this.formGroup.value.contact && this.contacts) {
        const createdContact = this.contacts.find(x => x.partyRole.iD === val);
        if (createdContact) {
          this.selectContact(createdContact);
        }
      }
    }
    onContactChange(this.formGroup.get('contactPartyRoleId').value);
    this.formGroup.get('contactPartyRoleId').valueChanges.subscribe((val) => {
      onContactChange(val);
    });

    if (this.formGroup.value.company && this.formGroup.value.company.PartyRoleID) {
      // means that party has been searched -- just select it
      this.selectCompany(this.formGroup.value.company, true);
    }

    if (this.formGroup.value.contact && this.formGroup.value.contact.partyRole) {
      // means that party has been searched -- just select it
      this.selectContact(this.formGroup.value.contact);
    }
  }

  populateTypes() {
    return Promise.all([
      this.typesService.getTypesList(PARTY_ROLE_SUITES_TYPES),
      this.typesService.getTypesList(PARTY_ROLE_TYPES),
      this.typesService.getTypesList(PARTY_ROLE_CATEGORIES),
    ]).then((result: any) => {
      const [ partyRoleSuitesTypes, partyRoleTypes, partyRoleCategories ] = result;
      this.partyRoleSuitesTypes = partyRoleSuitesTypes;
      this.partyRoleTypes = partyRoleTypes;
      this.partyRoleCategories = partyRoleCategories
      return result;
    });
  }

  resetForm() {
    this.formGroup.get('accountName').clearValidators();
    this.formGroup.get('accountName').updateValueAndValidity();
    this.formGroup.get('firstName').clearValidators();
    this.formGroup.get('firstName').updateValueAndValidity();
    this.formGroup.get('lastName').clearValidators();
    this.formGroup.get('lastName').updateValueAndValidity();
    this.formGroup.get('email').clearValidators();
    this.formGroup.get('email').updateValueAndValidity();
    this.formGroup.get('mobile').clearValidators();
    this.formGroup.get('mobile').updateValueAndValidity();

    this.formGroup.reset();
    this.resetCompanyAndContactValidators();
    this.submitted = false;
    this.displayAccountForm = false;
    this.displayPrimaryForm = false;
    this.displayPrimaryContacts = false;
  }

  addAccount() {
    // update form and validity

    this.formGroup.get('accountName').setValidators(Validators.required);
    this.formGroup.get('accountName').updateValueAndValidity();

    this.formGroup.get('companyPartyRoleId').patchValue(null);
    this.resultsPacket.companyPartyRoleId = null;
    this.crumbResults.emit(this.resultsPacket);
    this.displayAccountForm = true;
    this.addContact();
  }

  addContact() {
    // update form and validity
    this.formGroup.get('company').setValidators([]);
    this.formGroup.get('company').updateValueAndValidity();
    this.formGroup.get('contact').setValidators([]);
    this.formGroup.get('contact').updateValueAndValidity();

    this.formGroup.get('firstName').setAsyncValidators(fmcValidator('nameRequired'));
    this.formGroup.get('firstName').updateValueAndValidity();

    this.formGroup.get('lastName').setAsyncValidators(fmcValidator('nameRequired'));
    this.formGroup.get('lastName').updateValueAndValidity();

    this.formGroup.get('email').setAsyncValidators(fmcValidator('emailRequired'));
    this.formGroup.get('email').updateValueAndValidity();

    this.formGroup.get('mobile').setValidators(Validators.required);
    this.formGroup.get('mobile').updateValueAndValidity();

    this.formGroup.get('contactPartyRoleId').patchValue(null);
    this.resultsPacket.contactPartyRoleId = null;
    this.crumbResults.emit(this.resultsPacket);
    this.displayPrimaryForm = true;
  }

  displayName(object) {
    return (object) ? object.PartyName : null;
  }

  selectCompany(party, patchValue = false) {
    // console.log(party);
    this.selectedCompany = party;
    if (party && party.PartyRoleID) {
      this.resultsPacket.companyPartyRoleId = party.PartyRoleID;
      this.formGroup.get('companyPartyRoleId').patchValue(this.resultsPacket.companyPartyRoleId);
      this.crumbResults.emit(this.resultsPacket);
      this.clientService.getPartyRoleChildren(party.PartyRoleID)
        .subscribe(
          (contacts: any) => {
            this.contacts = contacts;

            if (patchValue) {
              const createdContact = this.contacts.find(x => x.partyRole.iD === this.resultsPacket.contactPartyRoleId)
              this.formGroup.get('contact').patchValue(createdContact);
            }
          },
          error => {
            console.log(error);
          });

      this.displayPrimaryContacts = true;
    } else {
      console.log('Error selecting party', party);
    }

    // // get a list of contacts
  }

  async selectContact(party) {
    this.selectedContact = party;
    console.log('Select Contact', party);
    this.resultsPacket.contactPartyRoleId = party.partyRole.iD;
    this.formGroup.get('contactPartyRoleId').patchValue(this.resultsPacket.contactPartyRoleId);
    this.crumbResults.emit(this.resultsPacket);

    // retrieve details based on the partyId
    this.contactDetails.address = await this.clientService.getPartyAddresses(party.party.iD).toPromise();
    this.contactDetails.email = await this.clientService.getPartyEmailAddresses(party.party.iD).toPromise();
    this.contactDetails.phone = await this.clientService.getPartyPhoneList(party.party.iD).toPromise();


  }

  searchPartyRoleSuite(query, selectPartyRoleId = null) {
    if (typeof query === 'object') {
      query = this.displayName(query);
    }

    this.loadingResults = true;

    if (!query) {
      query = this.formGroup.get('company').value;
    }

    if (query) {
      return this.clientService.searchPartyRole({
        prs: getIdFromTypes(this.partyRoleSuitesTypes, this.partyRoleSuite),
        prt: getIdFromTypes(this.partyRoleTypes, this.partyRoleType),
        prc: getIdFromTypes(this.partyRoleCategories, this.partyRoleCategory),
        name: query,
      }).subscribe((results) => {
        console.log('[search results]', results);
        this.partyRolesSuites = results;

        if (selectPartyRoleId) {
          const match = this.partyRolesSuites.find(pr => pr.PartyRoleID === selectPartyRoleId);
          if (match) {
            this.selectCompany(match, true);
          }
        }
        this.loadingResults = false;
      }, (err) => {
        console.log('error during search', err);
        this.loadingResults = false;
      });
    } else {
      this.loadingResults = false;
    }
  }


  createOwnerRelationship(partyRole) {
    const partyRelationshipObject = {
      parentPartyRoleId: partyRole.createdByPartyRoleID,
      childPartyRoleId: partyRole.iD,
      relationshipTypeId: OWNER_RELATIONSHIP_TYPE
    }

    // create party relationship
    this.clientService.createPartyMemberPartyRoleRelationship(partyRelationshipObject)
      .subscribe(
        () => {
          console.log('relationship created');
        },
        error => {
          console.log(error);
          this.notificationService.notify('error', 'We could not create the party relationship', 'Error' );
        });
  }

  createNewParty() {
    this.submitted = true;
    console.log('this.formGroup', this.formGroup);
    if (this.formGroup.valid) {
      if (this.resultsPacket.companyPartyRoleId && !this.resultsPacket.contactPartyRoleId) {
        this.createPartyContact();
      } else if (!this.resultsPacket.companyPartyRoleId && !this.resultsPacket.contactPartyRoleId) {
        this.createCompanyContact();
      }
    } else {
      this.formBuilderService.validateAllFormFields(this.formGroup);
    }
  }

  createCompanyContact() {
    const partyData: any = {
      company: {
        companyName: this.formGroup.get('accountName').value
      },
      party: {
        active: true,
        isInternal: false,
        partyTypeID: 2 // company
      }
    };

    this.loading = true;
    this.clientService.createParty(partyData)
      .subscribe((party: any) => {
        const partyRoleData: any = {
          partyID: party.party.iD,
          partyRoleTypeID: getIdFromTypes(this.partyRoleTypes, this.partyRoleType),
        };

        this.clientService.createPartyRole(partyRoleData)
          .subscribe((resultPartyRole: any) => {
            this.resultsPacket.companyPartyRoleId = resultPartyRole.iD;
            this.formGroup.patchValue({companyPartyRoleId: resultPartyRole.iD });
            // create the contact
            this.loading = false;
            this.createPartyContact();
            this.createOwnerRelationship(resultPartyRole);


          });
      }, (err) => {
        console.log('Error during Party Creation', err);
        this.loading = false;
        this.resetCompanyAndContactValidators();
      });
  }

  createPartyContact() {
    const partyData: any = {
      ignoreDuplicateCheck: true,
      party: {
        active: true,
        ultracsClientID: null,
        partyTypeID: 1 // individual
      },
      person: {
        firstName: this.formGroup.get('firstName').value,
        lastName: this.formGroup.get('lastName').value
      }
    };

    this.loading = true;
    this.clientService.createParty(partyData)
      .subscribe((party: any) => {
        const partyRoleData: any = {
          partyID: party.party.iD,
          partyRoleTypeID: getIdFromTypes(this.partyRoleTypes, 'general-contact')
        };


        // create email address entry
        const emailData: any = {
          emailTypeID: 1,
          EmailAddress: {
            emailAddress: this.formGroup.get('email').value
          },
          communicationTypeID: 10, // general
          locationTypeID: 1,
          partyID: party.party.iD
        };
        this.clientService.createPartyMemberEmail(emailData)
          .subscribe(email => {
            console.log('email created', email);
          });

        const mobileData: any = {
          TelephoneNumber: {
            telephoneNumber: this.formGroup.get('mobile').value,
          },
          telephoneTypeID: 3, // this is for mobile phone type
          TelephoneTypeID: 3, // this is for mobile phone type
          communicationTypeID: 10,
          locationTypeID: 1,
          preferredContact: false,
          partyID: party.party.iD,
          telephoneNumber: this.formGroup.get('mobile').value,
          iD: null,
          shouldDelete: false
        };
        this.clientService.createPartyMemberPhone(mobileData)
          .subscribe(mobile => {
            console.log('mobile created', mobile);
          });


        // create a new party role
        this.clientService.createPartyRole(partyRoleData)
          .subscribe((resultPartyRole: any) => {

            this.createOwnerRelationship(resultPartyRole);

            // relate the child to the parent as the employee (relationshipTypeId = 17)
            this.clientService.relateParty(this.resultsPacket.companyPartyRoleId, resultPartyRole.iD, 17)
              .subscribe((relationship: any) => {
                this.resultsPacket.contactPartyRoleId = resultPartyRole.iD;
                this.formGroup.get('contactPartyRoleId').patchValue(this.resultsPacket.contactPartyRoleId);
                this.crumbResults.emit(this.resultsPacket);
                this.selectCompany(this.selectedCompany, true); // select to refresh

                this.displayAccountForm = false;
                this.displayPrimaryForm = false;
                this.displayPrimaryContacts = true;

                this.loading = false;
                this.resetCompanyAndContactValidators();

              });

          });
      }, (err) => {
        console.log('Error during Party Creation', err);
        this.loading = false;
        this.resetCompanyAndContactValidators();
      });
  }

  private resetCompanyAndContactValidators(){
    // update form and validity
    // this.formGroup.get('company').setValidators([Validators.required]);
    // this.formGroup.get('company').updateValueAndValidity();
    // this.formGroup.get('contact').setValidators([Validators.required]);
    // this.formGroup.get('contact').updateValueAndValidity();
  }
}
